import Papa from "papaparse";

export type SourceType =
  | "shopify_orders"
  | "shopify_payouts"
  | "afterpay"
  | "zip_pay"
  | "paypal"
  | "bank_manual";

export interface NormalizedTransaction {
  source: SourceType;
  externalId?: string;
  orderId?: string;
  reference?: string;
  amount: string;
  currency: string;
  transactionDate: string;
  gateway: string;
  description?: string;
  rawData: object;
}

function parseDate(raw: string): string {
  if (!raw) return new Date().toISOString();
  const d = new Date(raw);
  if (!isNaN(d.getTime())) return d.toISOString();
  // Try DD/MM/YYYY
  const parts = raw.split("/");
  if (parts.length === 3) {
    const iso = `${parts[2]}-${parts[1].padStart(2, "0")}-${parts[0].padStart(2, "0")}`;
    const d2 = new Date(iso);
    if (!isNaN(d2.getTime())) return d2.toISOString();
  }
  return new Date().toISOString();
}

function parseAmount(raw: string | undefined | null): string {
  if (!raw) return "0.00";
  const cleaned = String(raw).replace(/[^0-9.\-]/g, "");
  const num = parseFloat(cleaned);
  return isNaN(num) ? "0.00" : num.toFixed(2);
}

function parseCsv(csvText: string): Record<string, string>[] {
  const result = Papa.parse<Record<string, string>>(csvText, {
    header: true,
    skipEmptyLines: true,
    transformHeader: (h) => h.trim(),
  });
  return result.data;
}

export function parseShopifyOrders(csvText: string): NormalizedTransaction[] {
  const rows = parseCsv(csvText);
  return rows
    .filter((r) => r["Name"] || r["Order"] || r["id"])
    .map((r) => {
      const orderId = r["Name"] || r["Order"] || r["id"] || "";
      const gateway = r["Payment Method"] || r["Payment Gateway"] || r["gateway"] || "shopify";
      const total = r["Total"] || r["Subtotal"] || r["total_price"] || "0";
      const date = r["Created at"] || r["Date"] || r["created_at"] || "";
      return {
        source: "shopify_orders" as SourceType,
        orderId,
        externalId: orderId,
        gateway: normalizeGatewayName(gateway),
        amount: parseAmount(total),
        currency: r["Currency"] || r["currency"] || "AUD",
        transactionDate: parseDate(date),
        description: r["Lineitem name"] || r["Notes"] || "",
        rawData: r,
      };
    });
}

export function parseShopifyPayouts(csvText: string): NormalizedTransaction[] {
  const rows = parseCsv(csvText);
  return rows
    .filter((r) => r["Type"] || r["type"])
    .map((r) => {
      const type = r["Type"] || r["type"] || "";
      const amount = r["Amount"] || r["Net amount"] || r["amount"] || "0";
      const date = r["Transaction Date"] || r["Date"] || r["Payout Date"] || r["date"] || "";
      const ref = r["Id"] || r["Payout ID"] || r["Reference"] || r["id"] || "";
      const source = r["Source"] || r["Source type"] || "shopify_payouts";
      return {
        source: "shopify_payouts" as SourceType,
        reference: ref,
        externalId: ref,
        gateway: normalizeGatewayName(source || "shopify_payouts"),
        amount: parseAmount(amount),
        currency: r["Currency"] || "AUD",
        transactionDate: parseDate(date),
        description: type,
        rawData: r,
      };
    });
}

export function parseAfterpay(csvText: string): NormalizedTransaction[] {
  const rows = parseCsv(csvText);
  return rows.map((r) => {
    const orderId =
      r["Order ID"] || r["Merchant Reference"] || r["merchant_reference"] || r["order_id"] || "";
    const amount = r["Payment Amount"] || r["Settlement Amount"] || r["Amount"] || r["amount"] || "0";
    const date = r["Created"] || r["Settlement Date"] || r["Date"] || r["date"] || "";
    const ref = r["Order Token"] || r["Token"] || r["id"] || "";
    return {
      source: "afterpay" as SourceType,
      orderId,
      externalId: ref,
      gateway: "afterpay",
      amount: parseAmount(amount),
      currency: r["Currency"] || "AUD",
      transactionDate: parseDate(date),
      description: r["Consumer"] || r["Description"] || "",
      rawData: r,
    };
  });
}

export function parseZipPay(csvText: string): NormalizedTransaction[] {
  const rows = parseCsv(csvText);
  return rows.map((r) => {
    const orderId =
      r["Order Reference"] || r["Merchant Order ID"] || r["order_id"] || "";
    const amount = r["Settlement Amount"] || r["Amount"] || r["amount"] || "0";
    const date = r["Created Date"] || r["Transaction Date"] || r["Date"] || r["date"] || "";
    const ref = r["Transaction ID"] || r["id"] || "";
    return {
      source: "zip_pay" as SourceType,
      orderId,
      externalId: ref,
      gateway: "zip_pay",
      amount: parseAmount(amount),
      currency: r["Currency"] || "AUD",
      transactionDate: parseDate(date),
      description: r["Description"] || r["Status"] || "",
      rawData: r,
    };
  });
}

export function parsePaypal(csvText: string): NormalizedTransaction[] {
  const rows = parseCsv(csvText);
  // Include fee rows (negative amounts) so they flow into Fees/Adjustments in
  // the Reckon summary. Only exclude internal hold adjustments that cancel out.
  return rows
    .filter((r) => {
      const type = (r["Type"] || r["Transaction Type"] || "").toLowerCase();
      return !type.includes("refund_from_hold") && !type.includes("hold");
    })
    .map((r) => {
      const type = (r["Type"] || r["Transaction Type"] || "").toLowerCase();
      const ref =
        r["Transaction ID"] || r["Invoice Number"] || r["transaction_id"] || "";
      const orderId = r["Invoice Number"] || r["Custom"] || r["custom"] || "";
      const gross = r["Gross"] || r["Amount"] || r["amount"] || "0";
      const fee = r["Fee"] || r["fee"] || "0";
      const date = r["Date"] || r["Transaction Date"] || r["date"] || "";
      const desc = r["Name"] || r["Subject"] || r["Description"] || type || "";
      // Use the fee column as a separate negative-amount row when it is non-zero
      // to ensure Reckon sees the fee as an adjustment.
      const amount = type.includes("fee") ? parseAmount(fee || gross) : parseAmount(gross);
      return {
        source: "paypal" as SourceType,
        externalId: ref,
        orderId: orderId || undefined,
        reference: ref,
        gateway: "paypal",
        amount,
        currency: r["Currency"] || "AUD",
        transactionDate: parseDate(date),
        description: desc,
        rawData: r,
      };
    });
}

export function parseBankManual(csvText: string): NormalizedTransaction[] {
  const rows = parseCsv(csvText);
  return rows.map((r) => {
    const amount =
      r["Amount"] || r["Credit"] || r["Debit"] || r["amount"] || "0";
    const date = r["Date"] || r["Transaction Date"] || r["date"] || "";
    const ref =
      r["Reference"] || r["Description"] || r["Narrative"] || r["id"] || "";
    return {
      source: "bank_manual" as SourceType,
      reference: ref,
      gateway: "bank_deposit",
      amount: parseAmount(amount),
      currency: r["Currency"] || "AUD",
      transactionDate: parseDate(date),
      description: ref,
      rawData: r,
    };
  });
}

function normalizeGatewayName(raw: string): string {
  const lower = raw.toLowerCase();
  if (lower.includes("afterpay")) return "afterpay";
  if (lower.includes("zip") || lower.includes("zippay")) return "zip_pay";
  if (lower.includes("paypal")) return "paypal";
  if (lower.includes("shopify")) return "shopify_payments";
  if (lower.includes("stripe")) return "stripe";
  if (lower.includes("bank") || lower.includes("manual") || lower.includes("deposit"))
    return "bank_deposit";
  return raw.toLowerCase().replace(/\s+/g, "_");
}

export const SOURCE_PARSERS: Record<SourceType, (csv: string) => NormalizedTransaction[]> = {
  shopify_orders: parseShopifyOrders,
  shopify_payouts: parseShopifyPayouts,
  afterpay: parseAfterpay,
  zip_pay: parseZipPay,
  paypal: parsePaypal,
  bank_manual: parseBankManual,
};

export const SOURCE_LABELS: Record<SourceType, string> = {
  shopify_orders: "Shopify Orders Export",
  shopify_payouts: "Shopify Payouts / Finance Summary",
  afterpay: "Afterpay Settlement",
  zip_pay: "Zip Pay Settlement",
  paypal: "PayPal Activity Report",
  bank_manual: "Manual / Bank Deposit (generic CSV)",
};
